/** external */
import _ from "lodash";
import React, { Fragment, useState, useRef, useEffect } from "react";
import classNames from "classnames";
import {
  Modal,
  SideNavigation,
  Button,
  useKeyboardShortcutsContext,
} from "@toddle-edu/ds-web";
import { connect } from "react-redux";

/** internal */
import Loading from "UIComponents/Loading";
import { setActiveFeatureWithKeyboardShortcuts } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  ENABLE_ALL_SHORTCUTS,
  ENABLE_SHORTCUT_PROMPTS,
} from "Constants/permissionConstants";
import { useI18n } from "Hooks";
import { checkIfShowNotificationInGlobalTrayEnabled } from "GlobalMenuTray/Utils";
import { getIsCurrentAcademicYear } from "Platform/modules/PlatformModule";
import {
  getIsLearningCoursesEnabledForOrgForCurrentAY,
  getIsLearningCoursesEnabledForAllProgramsForCurrentAY,
} from "LearningCourses/modules/Utils";
import {
  getOrganizationFreeStatus,
  getIsAdminPortalVisible,
} from "Platform/modules/PlatformUtils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getBehaviourNomenclatureFromCache } from "BehaviourConfiguration/modules/BehaviourConfigurationGraphqlHelpers";
import { getUserCurriculumPrograms } from "modules/CommonSelectors";
/** relative */
import classes from "./ShortcutManager.scss";
import {
  getSidebarOptionsMemoized,
  getShortcutListMemoized,
  getFilteredShortcutList,
} from "./ShortcutManagerUtils";
import { modalStyles } from "./ShortcutManagerStyles";
import ShortcutsPage from "./components/ShortcutsPage";
import ShortcutSettingsPage from "./components/ShortcutSettingsPage";

const ShortcutManager = props => {
  const {
    setActiveFeatureWithKeyboardShortcuts,
    updateSettings,
    getIsCurrentAcademicYear,
    isAdminPage,
    isAdminPortalAccessible,
    setToastMsg,
    userType,
    organizationId,
    behaviourNomenclatureConstants,
  } = props;
  const { t } = useI18n(["common"]);

  const testId = "shortcutManager";

  const {
    areShortcutsEnabled,
    areShortcutPromptsEnabled,
  } = useKeyboardShortcutsContext();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedSidebarOption, setSelectedSidebarOption] = useState(null);
  const [settingsValueLocal, setSettingsValueLocal] = useState({
    areShortcutsEnabled,
    areShortcutPromptsEnabled,
  });
  const [searchTextFilter, setSearchTextFilter] = useState("");

  const {
    areShortcutsEnabled: areShortcutsEnabledLocal,
    areShortcutPromptsEnabled: areShortcutPromptsEnabledLocal,
  } = settingsValueLocal;

  const areSettingsUpdated =
    areShortcutsEnabled !== areShortcutsEnabledLocal ||
    areShortcutPromptsEnabled !== areShortcutPromptsEnabledLocal;

  const updateSettingsValueLocal = ({
    areShortcutsEnabled = areShortcutsEnabledLocal,
    areShortcutPromptsEnabled = areShortcutPromptsEnabledLocal,
  }) => {
    // Prompts preference can only be turned on when shortcut preference is on
    setSettingsValueLocal({
      areShortcutsEnabled,
      areShortcutPromptsEnabled: areShortcutsEnabled
        ? areShortcutPromptsEnabled
        : false,
    });
  };

  const resetSettingsValueLocal = () => {
    setSettingsValueLocal({
      areShortcutsEnabled,
      areShortcutPromptsEnabled,
    });
  };

  const updateSettingsValue = async () => {
    const settings = [
      {
        name: ENABLE_ALL_SHORTCUTS,
        value: _.toString(areShortcutsEnabledLocal),
      },
      {
        name: ENABLE_SHORTCUT_PROMPTS,
        value: _.toString(areShortcutPromptsEnabledLocal),
      },
    ];

    setIsLoading(true);

    const isSuccess = await updateSettings({
      settings,
      showToastMsg: false,
      onSettingsUpdate: () => {
        setToastMsg({
          type: "success",
          msg: "toastMsgs:saved_successfully_with_label",
          locale_params: [{ key: "label", value: "common:shortcut_settings" }],
        });
      },
    });

    setIsLoading(false);

    if (isSuccess) {
      closeModal();
    }
  };

  const sectionHeaderRefs = useRef({});
  const isScrollManual = useRef(true);

  const isLearningCoursesEnabledInAnyProgram = getIsLearningCoursesEnabledForOrgForCurrentAY();
  const isLearningCoursesEnabledInAllPrograms = getIsLearningCoursesEnabledForAllProgramsForCurrentAY();

  const sidebarOptions = getSidebarOptionsMemoized({
    isLearningCoursesEnabledInAnyProgram,
    isLearningCoursesEnabledInAllPrograms,
  });

  const isNotificationIconVisibleInHeader =
    getIsCurrentAcademicYear() &&
    !checkIfShowNotificationInGlobalTrayEnabled() &&
    !isAdminPage;

  const shortcutList = getShortcutListMemoized({
    isNotificationIconVisibleInHeader,
    isAdminPortalAccessible,
    userType,
    organizationId,
    behaviourNomenclatureConstants,
    isLearningCoursesEnabledInAnyProgram,
    isLearningCoursesEnabledInAllPrograms,
  });

  const filteredShortcutList = getFilteredShortcutList({
    shortcutList,
    searchText: searchTextFilter,
  });

  const closeModal = () => {
    setActiveFeatureWithKeyboardShortcuts(null);
  };

  const setScrollToAutomatic = () => {
    isScrollManual.current = false;

    setTimeout(() => {
      isScrollManual.current = true;
    }, 300);
  };

  const updateSidebarOption = optionKey => {
    // Scroll to the selected section
    setScrollToAutomatic();
    sectionHeaderRefs?.current[optionKey]?.current?.scrollIntoView();
    setSelectedSidebarOption(optionKey);
  };

  // Handling selected sidebar option on mount and when search filter changes
  useEffect(() => {
    /** Not executing the useEffect when selected sidebar option is settings as we clear the search
     * filter manually in useEffect of ShortcutsPage component when user switches to settings page
     * which will trigger this useEffect and will move the selection back to first sidebar option */
    if (selectedSidebarOption === "settings") return;

    const firstSectionId = _.get(filteredShortcutList, "[0].id", "UNIVERSAL");

    setScrollToAutomatic();
    setSelectedSidebarOption(firstSectionId);
  }, [searchTextFilter]);

  const footerClasses = classNames(classes.footer, {
    [classes.footerVisible]: areSettingsUpdated,
  });

  return (
    <Modal dsVersion="2.0" width="1080px" height="640px" onClose={closeModal}>
      {isLoading ? (
        <Loading />
      ) : (
        <Fragment>
          <Modal.Header
            title={t("common:keyboard_shortcut_plural")}
            subtitle={t("common:shortcut_manager_modal_subtext")}
            hasSeparator
            testId={testId}
          />
          <Modal.Body style={modalStyles.body}>
            <div className={classes.body}>
              <div>
                <SideNavigation
                  dsVersion="2.0"
                  selectedOptionKey={selectedSidebarOption}
                  options={sidebarOptions}
                  testId={testId}
                  onClick={updateSidebarOption}
                />
              </div>

              {selectedSidebarOption === "settings" ? (
                <ShortcutSettingsPage
                  testId={testId}
                  areShortcutsEnabled={areShortcutsEnabledLocal}
                  areShortcutPromptsEnabled={areShortcutPromptsEnabledLocal}
                  updateSettingsValue={updateSettingsValueLocal}
                  resetSettingsValue={resetSettingsValueLocal}
                />
              ) : (
                <ShortcutsPage
                  testId={testId}
                  sectionHeaderRefs={sectionHeaderRefs}
                  setSidebarOption={setSelectedSidebarOption}
                  selectedSidebarOption={selectedSidebarOption}
                  shortcutList={filteredShortcutList}
                  searchTextFilter={searchTextFilter}
                  setSearchTextFilter={setSearchTextFilter}
                  isScrollManual={isScrollManual}
                />
              )}
            </div>

            {selectedSidebarOption === "settings" ? (
              <div className={footerClasses}>
                <Button
                  onClick={updateSettingsValue}
                  dsVersion="2.0"
                  shortcut={{ code: "Enter", shiftKey: true }}
                  disabled={!areSettingsUpdated}
                  testId={`${testId}-saveChanges`}
                >
                  {t("common:save_changes")}
                </Button>
              </div>
            ) : null}
          </Modal.Body>
        </Fragment>
      )}
    </Modal>
  );
};

const mapStateToProps = state => {
  const activeTab = _.get(state, "login.activeTab", "");
  const isAdminPage = activeTab === "admin";

  const { org_id: organizationId, user_type: userType } = getUserInfo();

  const organizationCurriculumPrograms = _.get(
    state,
    "platform.organizationCurriculumPrograms",
    []
  );

  const isOrganizationFree = getOrganizationFreeStatus({
    curriculumPrograms: organizationCurriculumPrograms,
  });

  const userCurriculumPrograms = getUserCurriculumPrograms();

  const isAdminPortalAccessible = getIsAdminPortalVisible({
    isOrganizationFree,
    userCurriculumPrograms,
  });

  const behaviourNomenclatureConstants = getBehaviourNomenclatureFromCache({
    organizationId,
  });

  return {
    isAdminPage,
    isAdminPortalAccessible,
    userType,
    organizationId,
    behaviourNomenclatureConstants,
  };
};

const mapActionCreators = {
  setActiveFeatureWithKeyboardShortcuts,
  updateSettings,
  getIsCurrentAcademicYear,
  setToastMsg,
};

export default connect(mapStateToProps, mapActionCreators)(ShortcutManager);
